import { Avatar, AvatarSize, theme, IconDictionary, Icon } from '@ketch-com/deck'
import { Box, Typography, Tooltip } from '@mui/material'
import { formatToK } from 'pages/dataSentry/utils/transformPieChartData'
import React from 'react'
import { DataSystemDetails } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'

export interface TopDataCollectingSystemItemProps {
  rank: number
  name?: string
  logo?: string
  code?: string
  collectionEvents?: number
  dataTypeCount?: number
  trackerCount?: number
  hasData: boolean
  totalDataTypeCount?: number
  usheredBy?: DataSystemDetails[]
  displayUsheredBy?: boolean
}

export const TopDataCollectingSystemItem: React.FC<TopDataCollectingSystemItemProps> = ({
  rank,
  name,
  logo,
  code,
  collectionEvents,
  trackerCount,
  hasData,
  usheredBy = [],
  displayUsheredBy = false,
}) => {
  const rankLabel = `${rank}${rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'}`
  const hasUsheredBy = usheredBy?.length > 0 && displayUsheredBy

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 140,
        position: 'relative',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-8px',
          right: '10px',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: 'inline-block',
            backgroundColor: '#f5f5f5',
            borderRadius: 11,
            px: 1.5,
            pt: 0.25,
            pb: 0.5,
          }}
        >
          <Typography variant="footnote" color={theme.palette.Text.Primary}>
            {rankLabel}
          </Typography>
        </Box>
        {hasUsheredBy && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.Black.o24}`,
              borderRadius: 11,
              marginTop: 0.25,
              display: 'flex',
              gap: 0.25,
              alignItems: 'center',
              padding: 0.5,
              marginLeft: -1,
              background: '#fff',
            }}
          >
            <Icon
              name={IconDictionary.OArrowCorner}
              width={20}
              height={20}
              iconColor={theme.palette.Black.o24}
              sx={{ transform: 'rotate(90deg)' }}
            />
            <Tooltip
              placement="bottom"
              title={
                <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                  {usheredBy?.map(system => <Typography key={system?.code}>{getSystemName(system)}</Typography>)}
                </Box>
              }
            >
              {usheredBy?.length === 1 ? (
                <Avatar
                  src={usheredBy?.[0]?.logoUrl || ''}
                  icon={usheredBy?.[0]?.logoUrl ? undefined : IconDictionary.NoLogo}
                  alt={usheredBy?.[0]?.name || ''}
                  textColor={usheredBy?.[0]?.logoUrl ? theme.palette.Black.o48 : theme.palette.Text.Secondary}
                  size={AvatarSize['small']}
                  isLogo
                />
              ) : (
                <Typography variant="label">{usheredBy?.length}</Typography>
              )}
            </Tooltip>
          </Box>
        )}
      </Box>

      {hasData ? (
        <>
          <Avatar
            src={logo || ''}
            icon={logo ? undefined : IconDictionary.NoLogo}
            alt={name || ''}
            borderColor={theme.palette.Black.o8}
            textColor={logo ? theme.palette.Black.o48 : theme.palette.Text.Secondary}
            size={AvatarSize['2xlarge']}
            sx={{ padding: '12px' }}
            isLogo
          />
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} textAlign="center">
            <Typography variant="label" color={theme.palette.Text.Primary}>
              {name || 'Unknown'}
            </Typography>
            {!name ? <Typography variant="footnote">{code}</Typography> : null}
            <Typography variant="smallBody" color={theme.palette.Text.Primary}>
              {collectionEvents !== undefined ? formatToK(collectionEvents) : 0} Collection Events
            </Typography>
            <Typography variant="smallBody" color={theme.palette.Text.Primary}>
              {trackerCount} Trackers
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Avatar
            icon="FUnknown"
            textColor={theme.palette.Text.Secondary}
            borderColor={theme.palette.Black.o8}
            size={AvatarSize['2xlarge']}
            sx={{ padding: '12px' }}
            isLogo
          />

          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="label" color={theme.palette.Text.Secondary}>
              Not Rated
            </Typography>
            <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
              Not enough data
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )
}
